import React from "react";
import styled from "styled-components";
import { ApplyIcon } from "../../../CommonUI/Icon/ApplyIcon";
import { DeleteGrayIcon } from "../../../CommonUI/Icon/DeleteGrayIcon";

import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { TestingConstants } from "../../../CommonUI/Testing/TestingConstants";
import {
    ExpressionReactContext,
    ExpressionRenderType,
    expressionSettings,
} from "../../../Expressions/Structure/ExpressionReactContext";
import { ExpressionTestResultColorContext } from "../../../Expressions/Testing/ExpressionTestResultColorContext";
import { ExpressionConfiguration } from "../../../rule-engine-core/Expressions/ExpressionConfiguration";

import { ExpressionView } from "../../../Expressions/ExpressionView";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import { EditColumnAction, EditColumnWrapper } from "../../Table/CommonStyles";
import { Error } from "../../Table/CommonUI/Error";
import { IntersectionRuleConfiguration } from "../../../rule-engine-core/Rule/Intersection/IntersectionRuleConfiguration";
import { ConfirmDialog } from "../../../CommonUI/ConfirmDialog";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

interface IIntersectionRuleColumn {
    expression: ExpressionConfiguration;
    ruleDefinition: IntersectionRuleConfiguration;
    direction: "x" | "y";
    index: number;
}

export const IntersectionRuleColumn = (props: IIntersectionRuleColumn) => {
    const testContext = React.useContext(TestContext);

    const [open, setOpen] = React.useState<boolean>(false);

    const onRemove = () => {
        if (props.expression.isEmpty || DataUtilities.getCookie("INTERSECTION_RULE_DELETE_CONFIRM") === "true") {
            props.ruleDefinition.removeExpression(props.expression, props.direction);

            if (props.direction === "x") {
                StateManager.propertyChanged(props.ruleDefinition, "xaxisExpressions");
            } else {
                StateManager.propertyChanged(props.ruleDefinition, "yaxisExpressions");
            }
        } else {
            setOpen(true);
        }
    };

    if (testContext) {
        const issues = props.expression.getDesignValidationIssues();
        const rulecolumnitem = testContext.findItem(props.expression._id);

        let testcolor: string | undefined;
        let borderColor: string | undefined;
        let backgroundColor: string | undefined;

        if (rulecolumnitem?.result === "true") {
            testcolor = TestingConstants.TEXT_SUCCESS;
            borderColor = TestingConstants.BORDER_SUCCESS;
        } else if (rulecolumnitem?.result === "false") {
            testcolor = TestingConstants.TEXT_FAIL;
            borderColor = TestingConstants.BORDER_FAIL;
        }

        if (rulecolumnitem?.isError) {
            backgroundColor = TestingConstants.BACKGROUND_FAIL;
            borderColor = TestingConstants.BORDER_FAIL;
        }

        return (
            <ExpressionTestResultColorContext.Provider value={testcolor}>
                <IntersectionRuleColumnRender
                    borderColor={borderColor}
                    backgroundColor={backgroundColor}
                    x={props.direction === "x" ? props.index + 3 : 1}
                    y={props.direction === "y" ? props.index + 2 : 0}
                >
                    {rulecolumnitem?.result === "true" && (
                        <PassedRender direction={props.direction}>
                            <ApplyIcon />
                        </PassedRender>
                    )}
                    <ExpressionReactContext.Provider value={expressionSettings[ExpressionRenderType.TEXT]}>
                        <ExpressionView
                            {...props}
                            hideValidation
                            color={{
                                expression: UIStyleConstants.BLUE_COLOR,
                                value: UIStyleConstants.GRAY_DARK_COLOR,
                                variable: UIStyleConstants.GRAY_DARK_COLOR,
                                missing: "" + UIStyleConstants.GRAY_LIGHTER_COLOR,
                            }}
                        />
                    </ExpressionReactContext.Provider>

                    <Error issues={issues} />
                </IntersectionRuleColumnRender>
            </ExpressionTestResultColorContext.Provider>
        );
    }

    return (
        <IntersectionRuleColumnRender>
            <ObserveState
                listenTo={props.expression}
                control={() => {
                    const issues = props.expression.getDesignValidationIssues();
                    return (
                        <>
                            <ExpressionView
                                {...props}
                                hideValidation
                                color={{
                                    expression: UIStyleConstants.BLUE_COLOR,
                                    value: UIStyleConstants.GRAY_DARK_COLOR,
                                    variable: UIStyleConstants.GRAY_DARK_COLOR,
                                    missing: "" + UIStyleConstants.GRAY_LIGHTER_COLOR,
                                }}
                                onDone={() => {
                                    StateManager.changed(props.expression);
                                    StateManager.changed(props.ruleDefinition);
                                }}
                            />
                            <Error issues={issues} />
                        </>
                    );
                }}
            />
            <EditColumnWrapper className="edit-wrapper">
                {props.direction === "x" && props.ruleDefinition.xaxisExpressions.length > 1 && (
                    <EditColumnAction onClick={onRemove}>
                        <DeleteGrayIcon />
                    </EditColumnAction>
                )}
                {props.direction === "y" && props.ruleDefinition.yaxisExpressions.length > 1 && (
                    <EditColumnAction onClick={onRemove}>
                        <DeleteGrayIcon />
                    </EditColumnAction>
                )}
            </EditColumnWrapper>
            <ConfirmDialog
                open={open}
                onConfirm={(dontAskAgain) => {
                    if (dontAskAgain) {
                        DataUtilities.setCookieValue("INTERSECTION_RULE_DELETE_CONFIRM", "true");
                    }

                    props.ruleDefinition.removeExpression(props.expression, props.direction);

                    if (props.direction === "x") {
                        StateManager.propertyChanged(props.ruleDefinition, "xaxisExpressions");
                    } else {
                        StateManager.propertyChanged(props.ruleDefinition, "yaxisExpressions");
                    }
                }}
                onCancel={() => setOpen(false)}
            />
        </IntersectionRuleColumnRender>
    );
};

const IntersectionRuleColumnRender = styled.div<{
    borderColor?: string;
    backgroundColor?: string;
    x?: number;
    y?: number;
}>`
    position: relative;
    min-width: 120px;
    min-height: 44px;

    width: 100%;
    height: 100%;

    background: ${(props) => props.backgroundColor || UIStyleConstants.WHITE_COLOR};
    border: 1px solid ${(props) => props.borderColor || UIStyleConstants.BLUE_COLOR};
    box-sizing: border-box;
    border-radius: 2px;

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};

    ${(props) =>
        props.x !== undefined
            ? `
                grid-column-start: ${props.x + 1};
                grid-column-end: ${props.x + 2};
            `
            : ``}
    ${(props) =>
        props.y !== undefined
            ? `
                grid-row-start: ${props.y + 1};
                grid-row-end: ${props.y + 2};
            `
            : ``}
  .edit-wrapper {
        opacity: 0;
        z-index: 2;
    }

    &:hover .edit-wrapper {
        opacity: 1;
    }
`;

const PassedRender = styled.div<{ direction: "x" | "y" }>`
    position: absolute;

    ${(props) =>
        props.direction === "x"
            ? `
                bottom: 100%;
                left: 50%;
                transform: translateX(-50%);
                
            `
            : `
                right: calc(100% + 36px);
                top: 50%;
                transform: translateY(-50%);
            `}
`;
